/**
 * v1.2 教务增强 C-2 + C-3 视角①：AI 学员指导进度解析卡（核心展示）
 *
 * 之前在 AcademicPages.tsx 内联定义，jsxDEV 模式下 function hoisting
 * 失效导致 GuidanceInsightCard not defined 学员详情整页白屏（rc2 fix ②）。
 * 拆到独立文件 + import 引用，避免 hoisting 依赖。
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { $ } from '../../lib/api';
import { fmtRelative } from '../../lib/format';
import { useAuthStore } from '../../store/auth';
import { HomeCard, Pill, Btn } from '../ui';

const STAGE_LABELS: Record<string, string> = {
  select_platform: '选平台',
  register_shop: '注册店铺',
  select_product: '选品',
  list_product: '产品上架',
  optimize: '产品优化',
  analyze_data: '产品数据查看分析',
  ad_placement: '广告投放',
  first_order: '出单',
  no_followup: '未激活',
};

export function GuidanceInsightCard({
  recordId, weworkBindStatus,
}: {
  recordId: number;
  weworkBindStatus?: string;
}) {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const canRegen = me && ['academic', 'academic_director', 'admin'].includes(me.role);

  const { data, isLoading } = useQuery<any>({
    queryKey: ['academic-guidance-insight', recordId],
    queryFn: () => $.get(`/academic/students/${recordId}/guidance-insight`),
    enabled: recordId > 0,
  });

  const regen = useMutation({
    mutationFn: () => $.post(`/academic/students/${recordId}/guidance-insight`, {}),
    onSuccess: () => {
      message.success('已重新生成');
      qc.invalidateQueries({ queryKey: ['academic-guidance-insight', recordId] });
    },
    onError: (e: any) => message.error(e?.message ?? '生成失败'),
  });

  const ij = data?.insightJson;
  const sources = data?.sourceSummary ?? '';

  return (
    <HomeCard title={
      <span>💡 学员指导进度解析{' '}
        {weworkBindStatus === 'unbound' && <Pill tone="warning">企微未绑定</Pill>}
        {weworkBindStatus === 'auto_matched' && <Pill tone="warning">自动匹配待确认</Pill>}
        {weworkBindStatus === 'confirmed' && <Pill tone="success">企微已绑</Pill>}
      </span> as any
    } action={
      canRegen ? (
        <Btn kind="default" disabled={regen.isPending} onClick={() => regen.mutate()}>
          {regen.isPending ? '生成中…' : '重新生成'}
        </Btn>
      ) : null
    }>
      {isLoading && <div style={{ padding: 16, color: 'var(--text-3)' }}>加载中…</div>}
      {!isLoading && !data && (
        <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
          尚未生成指导进度解析。点「重新生成」立即分析企微会话 + 教务任务 + 销售跟进。
        </div>
      )}
      {!isLoading && data && ij && (
        <div style={{ display: 'grid', gap: 12 }}>
          {ij.last_communication?.date && (
            <div style={{
              padding: 12, background: 'var(--primary-bg, #EAF2FF)',
              borderRadius: 8, fontSize: 13, color: 'var(--text-1)',
            }}>
              <strong>上次沟通 {ij.last_communication.date}：</strong>
              {ij.last_communication.summary ?? '—'}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
            <Pill tone="primary">当前阶段：{STAGE_LABELS[ij.current_guidance_stage] ?? ij.current_guidance_stage}</Pill>
          </div>
          <div style={{
            padding: 10, background: 'var(--bg-2, #F4F5F7)', borderRadius: 6,
            fontSize: 13, color: 'var(--text-1)', lineHeight: 1.6,
          }}>
            <strong>学员状态：</strong>{ij.student_status}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            <AiListBox title="✓ 已指导" items={ij.guidance_done ?? []} tone="success" />
            <AiListBox title="→ 下一步" items={ij.pending_next ?? []} tone="primary" />
            <AiListBox title="⚠ 待补承诺" items={ij.pending_items ?? []} tone="warning" />
          </div>
          {ij.progress_note && (
            <div style={{
              padding: 10, fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6,
              borderLeft: '3px solid var(--primary)', paddingLeft: 12,
            }}>
              {ij.progress_note}
            </div>
          )}
          <div style={{ fontSize: 11, color: 'var(--text-3)', textAlign: 'right' }}>
            {sources} · {data.model} · {data.latencyMs}ms ·
            生成于 {data.analyzedAt ? fmtRelative(data.analyzedAt) : '—'}
          </div>
        </div>
      )}
    </HomeCard>
  );
}

function AiListBox({ title, items, tone }: { title: string; items: string[]; tone: 'primary' | 'warning' | 'success' }) {
  return (
    <div style={{
      padding: 10, background: 'var(--bg-2, #F4F5F7)', borderRadius: 8,
      minHeight: 80,
    }}>
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 6 }}>{title}</div>
      {items.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>—</div>
      ) : (
        <ul style={{ paddingLeft: 16, margin: 0, fontSize: 13, color: 'var(--text-1)', lineHeight: 1.6 }}>
          {items.map((it, i) => <li key={i}>{it}</li>)}
        </ul>
      )}
    </div>
  );
}
